/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { InputNumber, Button, Row } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

/**
 * Extends InputNumber component to add custom increase/decrease buttons. Supports all InputNumber props
 * @param step number to increase/decrease value
 * @param buttonClassName override style for increase/decrease controls
 * @param inputNumberProps all supported InputNumber props
 * @returns {JSX.Element}
 */
const CustomInputNumber = ({ step = 1, buttonClassName, ...inputNumberProps }) => {
  // Keep state for custom increase/decrease buttons
  const [value, setValue] = useState(inputNumberProps.value || inputNumberProps.defaultValue);

  useEffect(() => {
    setValue(inputNumberProps.value);
  }, [inputNumberProps.value]);

  const handleOnChange = (value) => {
    if (inputNumberProps.onChange) {
      inputNumberProps.onChange(value);
    }
  };

  // Handle increment
  const handleIncrement = () => {
    let newValue = value ? value + step : step;
    if (inputNumberProps.max && newValue > inputNumberProps.max) {
      newValue = inputNumberProps.max;
    }
    handleOnChange(newValue);
  };

  // Handle decrement
  const handleDecrement = () => {
    let newValue = value ? value - step : inputNumberProps.min ? inputNumberProps.min : 0;
    if (inputNumberProps.min && newValue < inputNumberProps.min) {
      newValue = inputNumberProps.min;
    }
    handleOnChange(newValue);
  };

  return (
    <Row>
      <Button
        className={classNames('bg-layout', buttonClassName)}
        type={'text'} size={inputNumberProps.size}
        onClick={handleDecrement}
        icon={<MinusOutlined className={'fs-sm'} />}
        disabled={inputNumberProps.disabled}
      />
      <InputNumber
        {...inputNumberProps}
        className={classNames('mx-2 custom-number-input', inputNumberProps.className)}
        value={value}
        onChange={handleOnChange}
        controls={false}
        style={{ width: 60, ...inputNumberProps.style }}
      />
      <Button
        className={classNames('bg-layout', buttonClassName)}
        type={'text'} size={inputNumberProps.size}
        onClick={handleIncrement}
        icon={<PlusOutlined className={'fs-sm'} />}
        disabled={inputNumberProps.disabled}
      />
    </Row>
  );
};

CustomInputNumber.propTypes = {
  step: PropTypes.number,
  buttonClassName: PropTypes.string
};

export default CustomInputNumber;

